// src/components/LoginFlyout.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Popover, Box, TextField, InputAdornment, IconButton,
  Alert, Stack, Typography, Divider, CircularProgress
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {API_URL,API_UPLOAD_URL} from './config';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';

// campus.unifloo.com -> CAMPUS (one-level only). www.unifloo.com -> ""
function getCampusFromHost(hostname) {
  if (!hostname) return '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';

  const parts = hostname.split('.');
  // kiet.unifloo.com => ["kiet","unifloo","com"]
  if (parts.length === 3 && parts[1] === 'unifloo' && parts[2] === 'com') {
    return parts[0].toUpperCase();
  }
  return '';
}

export default function LoginFlyout({ buttonVariant = 'outlined' }) {
  const navigate = useNavigate();

  // Flyout control
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimer = useRef(null);

  // Credentials
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const hostCampus = getCampusFromHost(window.location.hostname);
  const isCampusLocked = Boolean(hostCampus);
  const [campusCode, setCampusCode] = useState(hostCampus || '');
  const [showPw, setShowPw] = useState(false);

  // 2FA
  const [authToken, setAuthToken] = useState('');
  const [showAuthenticator, setShowAuthenticator] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [isRegisteringAuthenticator, setIsRegisteringAuthenticator] = useState(false);
  const [isVerifyingAuthenticator, setIsVerifyingAuthenticator] = useState(false);

  // UX
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const loggedIn = !!localStorage.getItem('auth');

  const normalizeRoles = (roleField) => {
    if (!roleField) return [];
    if (Array.isArray(roleField)) return roleField.map(r => String(r).trim()).filter(Boolean);
    const s = String(roleField).trim();
    if (!s) return [];
    return s.split(',').map(r => r.trim()).filter(Boolean);
  };

  const isOnlyStudent = (roleField) => {
    const roles = normalizeRoles(roleField).map(r => r.toLowerCase());
    if (roles.length === 0) return false;
    const hasStudent = roles.includes('student');
    const hasNonStudent = roles.some(r => r !== 'student');
    return hasStudent && !hasNonStudent;
  };


  // Keep campusCode synced to host if on campus subdomain
  useEffect(() => {
    if (isCampusLocked) setCampusCode(hostCampus);
  }, [isCampusLocked, hostCampus]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  // Helpers: build correct endpoints without double /api
  // Recommended config.js: API_URL = "/api"
  const base = (API_URL || '').replace(/\/+$/, '');
  const postAuth = (path, body) => axios.post(`${base}/api/auth/${path}`, body);
  {/*
  const apiPost = (path, body) => {
    const base = (API_URL || '').replace(/\/+$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return axios.post(`${base}${p}`, body);
    };
  */}
  {/*
  const authPath = (p) => (API_URL === '/api' ? `/auth/${p}` : `/api/auth/${p}`);
    */}
  // If you keep API_URL="", change postAuth to: axios.post(`/api/auth/${path}`, body)

  const effectiveCampus = () =>
    (isCampusLocked ? hostCampus : campusCode).trim().toUpperCase();

  // Step 1: Initial login attempt
  const toBool = (v) => {
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes";
  }
  return false;
};

const handleLogin = async (e) => {
  e?.preventDefault?.();
  setError('');
  setQrCode('');
  setShowAuthenticator(false);
  setAuthToken('');
  setIsRegisteringAuthenticator(false);
  setIsVerifyingAuthenticator(false);

  setLoading(true);
  try {
    const res = await postAuth('login', {
      username,
      password,
      campusCode: effectiveCampus(),
      // ❌ DO NOT send is_2fa_enabled from frontend
    });

    const user = res.data?.user || {};
    const is2FAEnabled = toBool(user.is_2fa_enabled);

    const roleField = user.roles ?? user.role; // supports roles array if you add it later
    const onlyStudent = isOnlyStudent(roleField);

    // ✅ If 2FA is enabled for this account: always enforce 2FA flow
    if (is2FAEnabled) {
      if (res.data?.requiresAuthenticatorRegistration) {
        await registerAuthenticator();
        return;
      }
      if (res.data?.requiresAuthenticator) {
        setShowAuthenticator(true);
        setIsVerifyingAuthenticator(true);
        return;
      }

      // If backend still sends token here, that's a backend bug (but we'll block it)
      setError("2FA is enabled for this account, but /login did not ask for OTP/registration. Fix backend /login to return requiresAuthenticator* when is_2fa_enabled=1.");
      return;
    }

    // ✅ 2FA is NOT enabled: student-only can bypass ONLY if backend returned token
    if (onlyStudent) {
      if (res.data?.token) {
        localStorage.setItem('auth', res.data.token);
        setOpen(false);
        navigate('/home');
        return;
      }
      setError("Student-only login expected a token, but none was returned.");
      return;
    }

    // ✅ Non-students or mixed roles: enforce 2FA based on backend flags
    if (res.data?.requiresAuthenticatorRegistration) {
      await registerAuthenticator();
      return;
    }
    if (res.data?.requiresAuthenticator) {
      setShowAuthenticator(true);
      setIsVerifyingAuthenticator(true);
      return;
    }

    // If backend decides no 2FA and returns token, allow
    if (res.data?.token) {
      localStorage.setItem('auth', res.data.token);
      setOpen(false);
      navigate('/home');
      return;
    }

    setError('Unexpected login response.');
  } catch (err) {
    setError(err.response?.data?.message || 'Login failed');
  } finally {
    setLoading(false);
  }
};

  // Step 2: Register Authenticator (get QR)
  const registerAuthenticator = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await postAuth('register-authenticator', {
        username,
        campusCode: effectiveCampus(),
      });
      setQrCode(res.data.qrCode);
      setShowAuthenticator(true);
      setIsRegisteringAuthenticator(true);
      setIsVerifyingAuthenticator(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register Authenticator');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify registration code
  const handleVerifyRegisterAuthenticator = async () => {
    setError('');
    setLoading(true);
    try {
      await postAuth('verify-register-authenticator', {
        username,
        campusCode: effectiveCampus(),
        token: authToken,
      });
      setIsRegisteringAuthenticator(false);
      setIsVerifyingAuthenticator(true);
      setAuthToken('');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid authentication code for registration.');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Verify TOTP for login
  const handleVerifyAuthenticator = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await postAuth('verify-login', {
        username,
        password,
        campusCode: effectiveCampus(),
        token: authToken,
      });
      localStorage.setItem('auth', res.data.token);
      setOpen(false);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid authentication code');
    } finally {
      setLoading(false);
    }
  };


  const handlePasskeyLogin = async () => {
    setError('');

    if (!browserSupportsWebAuthn()) {
      setError('Passkeys are not supported by this browser or device.');
      return;
    }

    setPasskeyLoading(true);

    try {
      const passkeyBase = API_URL === '/api' ? base : `${base}/api`;

      // Discoverable passkey login: no username/password/campus required.
      const optionsRes = await axios.post(
        `${passkeyBase}/passkeys/authenticate/options`,
        {}
      );

      const { challengeId, options } = optionsRes.data || {};

      if (!challengeId || !options) {
        throw new Error('Invalid passkey options response.');
      }

      const authenticationResponse = await startAuthentication({
        optionsJSON: options,
      });

      const verifyRes = await axios.post(
        `${passkeyBase}/passkeys/authenticate/verify`,
        {
          challengeId,
          response: authenticationResponse,
          device: {
            platform: navigator.platform || '',
            userAgent: navigator.userAgent || '',
          },
        }
      );

      const token =
        verifyRes.data?.token ||
        verifyRes.data?.accessToken;

      if (!token) {
        throw new Error(
          'Passkey login succeeded, but no session token was returned.'
        );
      }

      localStorage.setItem('auth', token);

      if (verifyRes.data?.refreshToken) {
        localStorage.setItem(
          'refreshToken',
          verifyRes.data.refreshToken
        );
      }

      setOpen(false);
      navigate('/home');
    } catch (err) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error;

      if (err?.name === 'NotAllowedError') {
        setError(
          'Passkey sign-in was cancelled, timed out, or no matching passkey is available on this device.'
        );
      } else {
        setError(
          serverMessage ||
            err?.message ||
            'Passkey sign-in failed.'
        );
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const ContentLoginForm = (
    <Box component="form" onSubmit={handleLogin}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Sign in to UniFLOO
        </Typography>

        <TextField
          size="small"
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          autoFocus
        />

        <TextField
          size="small"
          label="Password"
          type={showPw ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPw((p) => !p)} edge="end">
                  {showPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          size="small"
          label="Campus Code"
          value={campusCode}
          onChange={(e) => setCampusCode(e.target.value)}
          required
          disabled={isCampusLocked}
          helperText={
            isCampusLocked
              ? `Campus locked from URL (${hostCampus}.unifloo.com)`
              : 'Enter your Campus Code'
          }
        />

        {error && <Alert severity="error">{error}</Alert>}

        <Button type="submit" variant="contained" disabled={loading || passkeyLoading}>
          {loading ? <CircularProgress size={20} /> : 'Login with Password'}
        </Button>

        <Divider>or</Divider>

        <Button
          type="button"
          variant="outlined"
          onClick={handlePasskeyLogin}
          //disabled={loading || passkeyLoading}
          sx={{ fontWeight: 700, textTransform: 'none' }}
        >
          {passkeyLoading ? <CircularProgress size={20} /> : 'Login with Passkey'}
        </Button>

        <Typography variant="caption" color="text.secondary" align="center">
          No account details required. Use Face ID, Touch ID, Windows Hello, or your device screen lock.
        </Typography>
      </Stack>
    </Box>
  );

  const ContentRegisterAuthenticator = (
    <Stack spacing={1.5}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Register Authenticator
      </Typography>
      <Typography variant="body2" color="text.secondary">
        1. Download any Authenticator app on your mobile.<br />
        (Ex: Microsoft Authenticator / Google Authenticator) <br />
        2. Scan this QR code on Authenticator app.<br />
        3. Enter the 6-digit code below.
      </Typography>

      {qrCode && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <img src={qrCode} alt="Google Authenticator QR Code" style={{ width: 180, height: 180 }} />
        </Box>
      )}

      <TextField
        size="small"
        label="Code from app"
        value={authToken}
        onChange={(e) => setAuthToken(e.target.value)}
        inputProps={{ inputMode: 'numeric' }}
        required
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={() => {
            setShowAuthenticator(false);
            setAuthToken('');
            setIsRegisteringAuthenticator(false);
            setIsVerifyingAuthenticator(false);
          }}
        >
          Back
        </Button>
        <Button variant="contained" onClick={handleVerifyRegisterAuthenticator} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Verify & Continue'}
        </Button>
      </Stack>
    </Stack>
  );

  const ContentVerifyLogin = (
    <Stack spacing={1.5}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Two-Step Verification
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Enter the code from your Authenticator app to finish signing in.
      </Typography>

      <TextField
        size="small"
        label="Authentication Code"
        value={authToken}
        onChange={(e) => setAuthToken(e.target.value)}
        inputProps={{ inputMode: 'numeric' }}
        required
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={() => {
            setShowAuthenticator(false);
            setAuthToken('');
            setIsRegisteringAuthenticator(false);
            setIsVerifyingAuthenticator(false);
          }}
        >
          Back
        </Button>
        <Button variant="contained" onClick={handleVerifyAuthenticator} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Verify & Login'}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    //<Box onMouseLeave={scheduleClose} sx={{ display: 'inline-flex' }}>
    <Box sx={{ display: 'inline-flex' }}>
      {loggedIn ? (
        <Button variant="contained" onClick={() => navigate('/home')} onMouseEnter={cancelClose}>
          Go to app
        </Button>
      ) : (
        <Button
          variant={buttonVariant}
          onMouseEnter={handleOpen}
          onClick={handleOpen}
        >
          Log in
        </Button>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          //onMouseEnter: cancelClose,
          //onMouseLeave: scheduleClose,
          sx: { p: 2, width: 360, borderRadius: 2 },
        }}
        disableRestoreFocus
      >
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Campus Portal
          </Typography>
          <Divider />
        </Stack>

        <Box sx={{ mt: 2 }}>
          {!showAuthenticator && ContentLoginForm}
          {showAuthenticator && isRegisteringAuthenticator && ContentRegisterAuthenticator}
          {showAuthenticator && isVerifyingAuthenticator && ContentVerifyLogin}
        </Box>
      </Popover>
    </Box>
  );
}
